import React, { useCallback, useEffect, useMemo, useState } from "react";
import { BookOpen, CheckCircle2, Layers, X } from "lucide-react";
import apiClient from "../../utils/apiClient";
import { enterLiveClass } from "../../utils/liveClassRouting";
import Shimmer from "../Common/Shimmer";
import { AppCard, AppCardContent, Callout, EmptyState, IconButton, ProgressBar } from "../ui";
import SyllabusDayCard from "./SyllabusDayCard";
import MapRecordingModal from "./MapRecordingModal";
import VideoPlayerModal from "../Videos/VideoPlayerModal";
import MapMaterialModal from "../Materials/MapMaterialModal";
import UploadMaterialModal from "../Materials/UploadMaterialModal";
import useMaterialViewer from "../Materials/useMaterialViewer";
import RescheduleDialog from "./schedule/RescheduleDialog";
import ScheduleNotice, { scheduleNeedsNotice } from "./schedule/ScheduleNotice";
import CarryOverDialog from "./schedule/CarryOverDialog";
import RepackDialog from "./schedule/RepackDialog";
import ModuleDivider from "./schedule/ModuleDivider";
import { dayKey, labelOf } from "./schedule/scheduleFormat";

/** Today in India, YYYY-MM-DD — a material's session date when its day has none. */
const todayIST = () => new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);

/** One day's materials, split into the whole day and each topic. */
function groupMaterials(items) {
  const day = [];
  const topics = {};
  for (const m of items) {
    if (m.linkedTopicIndex == null) day.push(m);
    else (topics[m.linkedTopicIndex] = topics[m.linkedTopicIndex] || []).push(m);
  }
  return { day, topics };
}

/**
 * TrainerTodaySession — the trainer's Syllabus tab: every day of the batch's
 * syllabus, the current one open, with its topics, recordings and materials.
 *
 * Start on a day opens the batch's class when it is not running — the server
 * does it with the same code as Start Class — so the page hands that to
 * `onStartDay` (the system check, then the classroom). While the class is
 * running, Start only starts the day.
 *
 * Materials go on a day or on one of its topics, uploaded straight there or
 * linked from what the batch already has.
 *
 * Props:
 *   batchId
 *   batchTiming   — { startTime, endTime } (optional)
 *   classLive     — the batch's class is running right now
 *   onStartDay    — (session) the page's system check + start; omit to start here
 *   canReschedule — show Reschedule (default true; the server decides)
 */
const TrainerTodaySession = ({ batchId, batchTiming, classLive = false, onStartDay, canReschedule = true }) => {
  const [syllabusData, setSyllabusData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [actionLoading, setActionLoading] = useState(null); // dayNumber saving
  const [expandedDays, setExpandedDays] = useState(() => new Set());

  const [recordingTarget, setRecordingTarget] = useState(null); // { dayNumber, topicIndex, label }
  const [playing, setPlaying] = useState(null); // the recording open in the player
  const [linkTarget, setLinkTarget] = useState(null); // { dayNumber, topicIndex, label }
  const [uploadTarget, setUploadTarget] = useState(null); // + sessionDate, topicName
  // { [dayNumber]: { loading, day: [], topics: { [i]: [] } } }
  const [materialsByDay, setMaterialsByDay] = useState({});
  const [removingId, setRemovingId] = useState(null);
  const { openMaterial, viewer } = useMaterialViewer();

  const [reschedule, setReschedule] = useState({ show: false, session: null, date: "" });
  const [carry, setCarry] = useState(null);
  const [repack, setRepack] = useState(null);

  const fetchSyllabus = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await apiClient.get(`/syllabus/batch/${batchId}/syllabus`);
      setSyllabusData(data);
      setLoadError("");
      const current = data.sessions?.find((s) => s.status !== "completed");
      if (current) setExpandedDays((prev) => (prev.has(current.dayNumber) ? prev : new Set(prev).add(current.dayNumber)));
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message || "";
      setSyllabusData(null);
      setLoadError(status === 404
        ? (msg.includes("Batch") ? "Batch not found." : "No syllabus has been added for this course yet.")
        : (msg || "The syllabus could not be loaded."));
    } finally {
      setLoading(false);
    }
  }, [batchId]);

  useEffect(() => { fetchSyllabus(); }, [fetchSyllabus]);

  const loadDayMaterials = useCallback(async (dayNumber) => {
    setMaterialsByDay((prev) => ({ ...prev, [dayNumber]: { ...(prev[dayNumber] || { day: [], topics: {} }), loading: true } }));
    try {
      const { data } = await apiClient.get(`/materials/batch/${batchId}/by-syllabus-day/${dayNumber}`);
      setMaterialsByDay((prev) => ({ ...prev, [dayNumber]: { loading: false, ...groupMaterials(data.data || []) } }));
    } catch {
      setMaterialsByDay((prev) => ({ ...prev, [dayNumber]: { ...(prev[dayNumber] || { day: [], topics: {} }), loading: false } }));
    }
  }, [batchId]);

  // A day's materials are read when it is first opened.
  useEffect(() => {
    expandedDays.forEach((day) => { if (!materialsByDay[day]) loadDayMaterials(day); });
  }, [expandedDays, materialsByDay, loadDayMaterials]);

  // After a link or unlink every day already read is read again — linking
  // moves a material off whichever day it was on before.
  const reloadLoadedMaterials = () => Object.keys(materialsByDay).forEach((d) => loadDayMaterials(Number(d)));

  const sessions = useMemo(() => syllabusData?.sessions || [], [syllabusData]);
  const currentDayNumber = sessions.find((s) => s.status !== "completed")?.dayNumber ?? null;
  const allCompleted = sessions.length > 0 && sessions.every((s) => s.status === "completed");
  const sessionOf = (dayNumber) => sessions.find((s) => s.dayNumber === dayNumber);

  const toggleExpand = (dayNumber) => setExpandedDays((prev) => {
    const next = new Set(prev);
    if (next.has(dayNumber)) next.delete(dayNumber);
    else next.add(dayNumber);
    return next;
  });

  const withAction = async (dayNumber, apiCall) => {
    setActionLoading(dayNumber);
    setError("");
    try {
      await apiCall();
      await fetchSyllabus(true);
    } catch (err) {
      setError(err?.response?.data?.message || "That did not work. Please try again.");
    } finally {
      setActionLoading(null);
    }
  };

  // ── Day actions ──
  const handleStart = (session) => {
    // A class not yet running opens through the page's own Start flow (system
    // check, topic, then the classroom).
    if (!classLive && onStartDay) {
      onStartDay(session);
      return;
    }
    withAction(session.dayNumber, async () => {
      const { data } = await apiClient.post(`/syllabus/batch/${batchId}/syllabus/${session.dayNumber}/start`);
      if (data.classStarted) enterLiveClass(batchId, data);
    });
  };

  const handleEnd = (dayNumber) => withAction(dayNumber, async () => {
    const { data } = await apiClient.post(`/syllabus/batch/${batchId}/syllabus/${dayNumber}/end`);
    const ended = data?.session;
    if (canReschedule && ended?.topics?.some((t) => t.status !== "completed")) {
      const row = sessionOf(dayNumber);
      setCarry({ ...row, ...ended, label: row?.label });
    }
  });

  const dayCall = (dayNumber, action) => withAction(dayNumber, () =>
    apiClient.post(`/syllabus/batch/${batchId}/syllabus/${dayNumber}/${action}`));

  const handleToggleTopic = (dayNumber, topicIndex) => withAction(dayNumber, () =>
    apiClient.post(`/syllabus/batch/${batchId}/syllabus/${dayNumber}/topic/${topicIndex}/toggle`));

  // ── Recordings (the batch's Videos) on a day or a topic ──
  const recordingPath = (dayNumber, topicIndex) => (topicIndex == null
    ? `/syllabus/batch/${batchId}/syllabus/${dayNumber}/recording`
    : `/syllabus/batch/${batchId}/syllabus/${dayNumber}/topic/${topicIndex}/recording`);

  const mappedHere = (target) => {
    if (!target) return [];
    const session = sessionOf(target.dayNumber);
    const list = target.topicIndex == null ? session?.recordings : session?.topics?.[target.topicIndex]?.recordings;
    return (list || []).map((r) => r.videoId).filter(Boolean);
  };

  const handleMapRecording = async (videoId) => {
    if (!recordingTarget) return;
    await apiClient.post(recordingPath(recordingTarget.dayNumber, recordingTarget.topicIndex), { videoId });
    setRecordingTarget(null);
    setNotice("Recording mapped.");
    await fetchSyllabus(true);
  };

  const handleRemoveRecording = async (dayNumber, recording, topicIndex) => {
    const ref = recording.videoId || recording.recordingId;
    setRemovingId(ref);
    setError("");
    try {
      await apiClient.delete(`${recordingPath(dayNumber, topicIndex)}/${ref}`);
      setNotice("Recording taken off. It is still on the batch's Videos tab.");
      await fetchSyllabus(true);
    } catch (err) {
      setError(err?.response?.data?.message || "The recording could not be removed.");
    } finally {
      setRemovingId(null);
    }
  };

  // ── Materials ──
  const targetFor = (session, topicIndex) => {
    const topic = topicIndex == null ? null : session.topics?.[topicIndex];
    return {
      dayNumber: session.dayNumber,
      topicIndex,
      label: topic ? `${topic.name} (${labelOf(session)})` : `${labelOf(session)} — ${session.title}`,
      sessionDate: dayKey(session.plannedDate) || todayIST(),
      topicName: topic?.name || session.title || "",
    };
  };

  const linkedHere = (target) => {
    const day = materialsByDay[target?.dayNumber];
    if (!day) return [];
    const items = target.topicIndex == null ? day.day : day.topics?.[target.topicIndex] || [];
    return items.map((m) => m._id);
  };

  const handleLinkMaterial = async (materialId) => {
    if (!linkTarget) return;
    await apiClient.put(`/materials/${materialId}/link-syllabus-topic`, {
      syllabusDay: linkTarget.dayNumber,
      topicIndex: linkTarget.topicIndex,
    });
    setLinkTarget(null);
    setNotice("Material linked.");
    reloadLoadedMaterials();
  };

  const handleRemoveMaterial = async (material) => {
    setRemovingId(material._id);
    setError("");
    try {
      await apiClient.put(`/materials/${material._id}/link-syllabus-topic`, { syllabusDay: null, topicIndex: null });
      setNotice(`“${material.title}” was taken off the syllabus. It is still in the batch's materials.`);
      reloadLoadedMaterials();
    } catch (err) {
      setError(err?.response?.data?.message || "The material could not be removed.");
    } finally {
      setRemovingId(null);
    }
  };

  if (loading) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-3" aria-busy="true">
        <Shimmer height={64} />
        <Shimmer height={88} count={3} />
      </div>
    );
  }

  if (!sessions.length) {
    return (
      <EmptyState
        icon={BookOpen}
        title={loadError ? "No syllabus yet" : "The batch has not started"}
        description={loadError || "Syllabus days appear here once the batch is started."}
      />
    );
  }

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      {error && (
        <Callout
          tone="danger"
          role="alert"
          action={<IconButton label="Dismiss" icon={X} size="xs" onClick={() => setError("")} />}
        >
          {error}
        </Callout>
      )}
      {notice && (
        <Callout tone="success" action={<IconButton label="Dismiss" icon={X} size="xs" onClick={() => setNotice("")} />}>
          {notice}
        </Callout>
      )}

      {scheduleNeedsNotice(syllabusData.schedule) && (
        <ScheduleNotice
          schedule={syllabusData.schedule}
          sessions={sessions}
          onRepair={canReschedule ? (session, date) => setReschedule({ show: true, session, date }) : undefined}
        />
      )}

      <AppCard>
        <AppCardContent className="tw-flex tw-flex-col tw-gap-2 tw-p-4">
          <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
            <span className="tw-inline-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-content">
              <Layers size={16} aria-hidden="true" className="tw-text-content-muted" />
              Overall progress
            </span>
            <span className="tw-text-[0.8125rem] tw-text-content-muted">
              {syllabusData.completedDays}/{syllabusData.totalDays} days · {syllabusData.progressPercent}%
            </span>
          </div>
          <ProgressBar value={syllabusData.progressPercent} color={allCompleted ? "success" : "primary"} size="md" />
          {allCompleted && (
            <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-[0.8125rem] tw-font-medium tw-text-success">
              <CheckCircle2 size={15} aria-hidden="true" /> Every day of the syllabus is completed.
            </span>
          )}
        </AppCardContent>
      </AppCard>

      <ol className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-3 tw-p-0">
        {sessions.map((session, i) => {
          const prev = sessions[i - 1];
          const startsModule = !!session.moduleTitle && (!prev || prev.moduleNumber !== session.moduleNumber);
          return (
            <React.Fragment key={session.dayNumber}>
              {startsModule && (
                <li className="tw-list-none">
                  <ModuleDivider number={session.moduleNumber} title={session.moduleTitle} />
                </li>
              )}
              <SyllabusDayCard
                session={session}
                isCurrent={session.dayNumber === currentDayNumber}
                isExpanded={expandedDays.has(session.dayNumber)}
                onToggleExpand={() => toggleExpand(session.dayNumber)}
                classLive={classLive}
                onStart={() => handleStart(session)}
                onEnd={() => handleEnd(session.dayNumber)}
                onRestart={() => dayCall(session.dayNumber, "restart")}
                onReopen={() => dayCall(session.dayNumber, "reopen")}
                onMarkCompleted={() => dayCall(session.dayNumber, "mark-completed")}
                onToggleTopic={(topicIndex) => handleToggleTopic(session.dayNumber, topicIndex)}
                onMapRecording={(topicIndex) => setRecordingTarget(targetFor(session, topicIndex))}
                onOpenRecording={setPlaying}
                onRemoveRecording={(rec, topicIndex) => handleRemoveRecording(session.dayNumber, rec, topicIndex)}
                onReschedule={canReschedule ? () => setReschedule({ show: true, session, date: "" }) : undefined}
                onCarryOver={canReschedule ? () => setCarry(session) : undefined}
                materials={materialsByDay[session.dayNumber] || { loading: true }}
                onUploadMaterial={(topicIndex) => setUploadTarget(targetFor(session, topicIndex))}
                onLinkMaterial={(topicIndex) => setLinkTarget(targetFor(session, topicIndex))}
                onOpenMaterial={openMaterial}
                onRemoveMaterial={handleRemoveMaterial}
                removingId={removingId}
                actionLoading={actionLoading === session.dayNumber}
                batchTiming={batchTiming}
              />
            </React.Fragment>
          );
        })}
      </ol>

      <MapRecordingModal
        show={!!recordingTarget}
        batchId={batchId}
        targetLabel={recordingTarget?.label}
        alreadyMappedIds={mappedHere(recordingTarget)}
        onMap={handleMapRecording}
        onClose={() => setRecordingTarget(null)}
      />

      {playing && (
        <VideoPlayerModal
          show
          role="instructor"
          batchId={batchId}
          video={{ _id: playing.videoId, title: playing.title, recordedAt: playing.recordedAt, batch: batchId }}
          onClose={() => setPlaying(null)}
        />
      )}

      <MapMaterialModal
        show={!!linkTarget}
        batchId={batchId}
        targetLabel={linkTarget?.label}
        hint="A material sits on one day or topic — linking it here moves it from wherever it is linked now."
        alreadyLinkedIds={linkedHere(linkTarget)}
        onLink={handleLinkMaterial}
        onClose={() => setLinkTarget(null)}
      />

      <UploadMaterialModal
        show={!!uploadTarget}
        batchId={batchId}
        hideLevel
        syllabusDay={uploadTarget?.dayNumber ?? null}
        topicIndex={uploadTarget?.topicIndex ?? null}
        targetLabel={uploadTarget?.label}
        defaultSessionDate={uploadTarget?.sessionDate || ""}
        defaultTopic={uploadTarget?.topicName || ""}
        onClose={() => setUploadTarget(null)}
        onSuccess={() => {
          const day = uploadTarget?.dayNumber;
          setNotice("Material uploaded.");
          if (day != null) loadDayMaterials(day);
        }}
      />

      <RescheduleDialog
        show={reschedule.show}
        onHide={() => setReschedule({ show: false, session: null, date: "" })}
        batchId={batchId}
        session={reschedule.session}
        initialDate={reschedule.date}
        today={syllabusData.schedule?.today}
        onSaved={(message) => { setNotice(message); fetchSyllabus(true); }}
      />
      <CarryOverDialog
        show={!!carry}
        onHide={() => setCarry(null)}
        batchId={batchId}
        session={carry}
        onDone={(message) => { setNotice(message); fetchSyllabus(true); }}
        onOverrun={(overrun) => setRepack(overrun)}
      />
      <RepackDialog
        show={!!repack}
        onHide={() => setRepack(null)}
        batchId={batchId}
        overrun={repack}
        onDone={(message) => { setNotice(message); fetchSyllabus(true); }}
      />

      {viewer}
    </div>
  );
};

export default TrainerTodaySession;
